import React from 'react'
import { Loading, Message } from '@workwhile/ui'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import { usePaginationQueryState } from 'lib/nuqs/usePaginationQueryState'
import { useJetCallReportsQuery } from 'queries/jet/useJetCallReportsQuery'
import { useJetCallReportStatusSummaryQuery } from 'queries/jet/useJetCallReportStatusSummaryQuery'
import type { JetCallReportStatus } from 'pages/Jet/shared/constants/status'
import { useJetStatusFiltersQueryState } from '../hooks/useJetStatusFiltersQueryState'

type JetStatusFilters = ReturnType<typeof useJetStatusFiltersQueryState>[0]

export const parseSearchTokens = (search: string): string[] | undefined => {
  const tokens = search
    .trim()
    .split(/[\s,]+/)
    .filter(Boolean)
  return tokens.length ? tokens : undefined
}

type JetStatusContextState = {
  state: {
    data: NonNullable<ReturnType<typeof useJetCallReportsQuery>['data']>
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
    filters: JetStatusFilters
    pagination: {
      pageIndex: number
      pageSize: number
    }
    statusSummary: NonNullable<
      ReturnType<typeof useJetCallReportStatusSummaryQuery>['data']
    >['items']
  }
  actions: {
    refetch: () => void
    setFilters: (update: Partial<JetStatusFilters>) => void
    setPagination: ReturnType<typeof usePaginationQueryState>[1]
    setStatusFilter: (status: JetCallReportStatus) => void
  }
}

const [Context, useJetStatusContextValue] =
  createContext<JetStatusContextState>({ name: 'JetStatusContext' })

export const JetStatusProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [filters, setFilters] = useJetStatusFiltersQueryState()
  const [pagination, setPagination] = usePaginationQueryState()

  const setFiltersAndResetPage = (update: Partial<JetStatusFilters>) => {
    setFilters(update)
    setPagination({ pageIndex: 0 })
  }

  const setStatusFilter = (status: JetCallReportStatus) => {
    const isActive =
      filters.statuses?.length === 1 && filters.statuses[0] === status
    setFiltersAndResetPage({ statuses: isActive ? null : [status] })
  }

  const {
    data,
    isPending,
    isError,
    error,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useJetCallReportsQuery({
    page: pagination.pageIndex,
    pageSize: pagination.pageSize,
    daysAgo: filters.daysAgo,
    statuses: filters.statuses,
    search: parseSearchTokens(filters.search),
  })

  const { data: statusSummaryData } = useJetCallReportStatusSummaryQuery({
    daysAgo: filters.daysAgo,
  })

  if (isPending) {
    return <Loading />
  }

  if (isError && !data) {
    return (
      <Message variant="error" m={4} borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  if (!data) {
    return null
  }

  const value: JetStatusContextState = {
    state: {
      data,
      dataUpdatedAt,
      isFetching,
      isError,
      error: parseErrorMessage(error),
      filters,
      pagination,
      statusSummary: statusSummaryData?.items ?? [],
    },
    actions: {
      refetch,
      setFilters: setFiltersAndResetPage,
      setPagination,
      setStatusFilter,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useJetStatusContextValue }
